/**
 * Predictive Analytics Page
 * AI-powered sales and ROI forecasting with charts
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Psychology,
  Timeline,
  Speed,
  AutoGraph,
  Info,
  Refresh
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import api from '../../services/api';

const PredictiveAnalytics = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [timeHorizon, setTimeHorizon] = useState('quarter');
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState({
    predictedRevenue: 0,
    predictedGrowth: 0,
    confidence: 0,
    topOpportunity: ''
  });
  
  // Table state
  const [orderBy, setOrderBy] = useState('change');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Call the predictive analytics API
        const response = await api.post('/predictive-analytics/predict-sales', {
          horizon: timeHorizon
        });
        const data = response.data;
        
        // Transform API data to match component format
        const transformedPredictions = [];
        
        if (data.predictions) {
          // If API returns predictions array
          data.predictions.forEach((pred, index) => {
            transformedPredictions.push({
              _id: index.toString(),
              category: pred.category || pred.metric || 'Metric',
              current: pred.current || pred.baseline || 0,
              predicted: pred.predicted || pred.forecast || 0,
              change: pred.change || pred.growthRate || 0,
              confidence: pred.confidence || 75,
              trend: (pred.change || pred.growthRate || 0) >= 0 ? 'up' : 'down'
            });
          });
        }
        
        // If no predictions from API, show summary-based predictions
        if (transformedPredictions.length === 0 && data.summary) {
          transformedPredictions.push({
            _id: '1',
            category: 'Total Revenue',
            current: data.summary.currentRevenue || 0,
            predicted: data.summary.predictedRevenue || 0,
            change: data.summary.growthRate || 0,
            confidence: data.summary.confidence || 75,
            trend: (data.summary.growthRate || 0) >= 0 ? 'up' : 'down'
          });
        }
        
        setPredictions(transformedPredictions);
        setSummary({
          predictedRevenue: data.summary?.predictedRevenue || data.predictedRevenue || 0,
          predictedGrowth: data.summary?.growthRate || data.growthRate || 0,
          confidence: data.summary?.confidence || data.confidence || 75,
          topOpportunity: data.summary?.topOpportunity || data.topOpportunity || 'Revenue Growth'
        });
      } catch (error) {
        console.error('Error fetching predictions:', error);
        enqueueSnackbar('Failed to load predictive analytics', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [timeHorizon, enqueueSnackbar]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatValue = (category, value) => {
    if (category === 'Total Revenue') return formatCurrency(value);
    if (category === 'Promotion ROI' || category === 'Trade Spend Efficiency') return `${value}%`;
    if (category === 'Market Share') return `${value}%`;
    return value;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'success';
    if (confidence >= 60) return 'warning';
    return 'error';
  };

  // Sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedPredictions = [...predictions].sort((a, b) => {
    const aVal = a[orderBy] || 0;
    const bVal = b[orderBy] || 0;
    return order === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Chart data - forecast trend
  const forecastChartData = [
    { name: 'Current', value: predictions[0]?.current || 0 },
    { name: 'Q1 Forecast', value: (predictions[0]?.current || 0) * 1.02 },
    { name: 'Q2 Forecast', value: (predictions[0]?.current || 0) * 1.05 },
    { name: 'Q3 Forecast', value: (predictions[0]?.current || 0) * 1.08 },
    { name: 'Predicted', value: predictions[0]?.predicted || 0 }
  ];

  // Comparison chart data
  const comparisonChartData = predictions.map(p => ({
    name: p.category?.substring(0, 12) || 'Metric',
    current: p.current / 1000,
    predicted: p.predicted / 1000,
    change: p.change
  }));

  // Refresh predictions
  const handleRefresh = () => {
    setLoading(true);
    // Re-trigger the useEffect by changing a dependency
    setTimeHorizon(prev => prev);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/dashboard">Home</Link>
        <Link color="inherit" href="/analytics">Insights</Link>
        <Typography color="text.primary">Predictive Analytics</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Predictive Analytics
          </Typography>
          <Typography variant="body2" color="textSecondary">
            AI-powered forecasting and trend predictions
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Horizon</InputLabel>
          <Select
            value={timeHorizon}
            onChange={(e) => setTimeHorizon(e.target.value)}
            label="Time Horizon"
          >
            <MenuItem value="month">Next Month</MenuItem>
            <MenuItem value="quarter">Next Quarter</MenuItem>
            <MenuItem value="year">Next Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* AI Badge */}
      <Alert 
        severity="info" 
        icon={<Psychology />}
        sx={{ mb: 3 }}
      >
        Predictions are generated using machine learning models trained on your historical data. 
        Confidence scores indicate the reliability of each prediction.
      </Alert>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <AutoGraph color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h5">{formatCurrency(summary.predictedRevenue)}</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" color="textSecondary">Predicted Revenue</Typography>
                    <Tooltip title="ML-based revenue forecast for selected time horizon">
                      <Info sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <TrendingUp color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h5">+{summary.predictedGrowth}%</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" color="textSecondary">Predicted Growth</Typography>
                    <Tooltip title="Expected growth rate based on historical trends">
                      <Info sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Speed color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h5">{summary.confidence}%</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" color="textSecondary">Avg Confidence</Typography>
                    <Tooltip title="Model confidence based on data quality and historical accuracy">
                      <Info sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Timeline color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">{summary.topOpportunity}</Typography>
                  <Typography variant="body2" color="textSecondary">Top Opportunity</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Revenue Forecast Trend</Typography>
            <Box sx={{ height: 300 }}>
              {forecastChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={forecastChartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                    <RechartsTooltip formatter={(value) => [`$${(value / 1000).toFixed(1)}K`, 'Revenue']} />
                    <Area type="monotone" dataKey="value" stroke="#4caf50" fill="#4caf5033" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="textSecondary">No forecast data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Current vs Predicted</Typography>
            <Box sx={{ height: 300 }}>
              {comparisonChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} fontSize={12} />
                    <YAxis label={{ value: 'Value ($K)', angle: -90, position: 'insideLeft' }} />
                    <RechartsTooltip formatter={(value) => [`$${value}K`, '']} />
                    <Legend />
                    <Bar dataKey="current" fill="#2196f3" name="Current" />
                    <Bar dataKey="predicted" fill="#4caf50" name="Predicted" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="textSecondary">No comparison data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Predictions Table */}
      <Paper>
        <Box p={2} borderBottom={1} borderColor="divider" display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Detailed Predictions</Typography>
          <Tooltip title="Refresh predictions">
            <IconButton onClick={handleRefresh} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'current'}
                    direction={orderBy === 'current' ? order : 'asc'}
                    onClick={() => handleSort('current')}
                  >
                    Current
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'predicted'}
                    direction={orderBy === 'predicted' ? order : 'asc'}
                    onClick={() => handleSort('predicted')}
                  >
                    Predicted
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'change'}
                    direction={orderBy === 'change' ? order : 'asc'}
                    onClick={() => handleSort('change')}
                  >
                    Change
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'confidence'}
                    direction={orderBy === 'confidence' ? order : 'asc'}
                    onClick={() => handleSort('confidence')}
                  >
                    Confidence
                  </TableSortLabel>
                </TableCell>
                <TableCell>Trend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPredictions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary" py={4}>
                      No prediction data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedPredictions.map((prediction) => (
                  <TableRow key={prediction._id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{prediction.category}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(prediction.category, prediction.current)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {formatValue(prediction.category, prediction.predicted)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={prediction.change >= 0 ? <TrendingUp /> : <TrendingDown />}
                        label={`${prediction.change >= 0 ? '+' : ''}${prediction.change}%`}
                        size="small"
                        color={prediction.change >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={prediction.confidence}
                          color={getConfidenceColor(prediction.confidence)}
                          sx={{ width: 60, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2">{prediction.confidence}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={prediction.trend === 'up' ? 'Upward' : 'Downward'}
                        size="small"
                        color={prediction.trend === 'up' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default PredictiveAnalytics;
